import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, MagnifyingGlass } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const AddGroupMembers = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [availableUsers, setAvailableUsers] = useState([]);
  const [currentMembers, setCurrentMembers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAvailableUsers();
  }, []);

  const fetchAvailableUsers = async () => {
    try {
      // First fetch current group members
      const groupRes = await axios.get(`${API}/api/groups/${groupId}`);
      const currentMemberIds = groupRes.data.members.map(m => m.user_id);
      setCurrentMembers(currentMemberIds);
      
      // Fetch all users (customers + staff)
      const [customersRes, staffRes] = await Promise.all([
        axios.get(`${API}/api/users/all-customers`),
        axios.get(`${API}/api/users/all-staff`)
      ]);
      
      const allUsers = [...(customersRes.data || []), ...(staffRes.data || [])];
      
      // Filter out users who are already group members
      const usersNotInGroup = allUsers.filter(u => !currentMemberIds.includes(u.id));
      
      setAvailableUsers(usersNotInGroup);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
      setLoading(false);
    }
  };

  const toggleUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      // If all are selected, deselect all
      setSelectedUsers([]);
    } else {
      // Select all filtered users
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  const handleAddMembers = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one member');
      return;
    }

    try {
      // Add members one by one (backend accepts single user per call with query params)
      let successCount = 0;
      let failedCount = 0;
      
      for (const userId of selectedUsers) {
        try {
          await axios.post(`${API}/api/groups/${groupId}/add-member?user_id=${userId}&added_by=${user.id}`);
          successCount++;
        } catch (err) {
          console.error(`Failed to add user ${userId}:`, err);
          failedCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} member(s) added successfully!`);
      }
      if (failedCount > 0) {
        toast.error(`${failedCount} member(s) failed to add`);
      }
      
      navigate(`/groups/${groupId}/dashboard`);
    } catch (error) {
      console.error('Failed to add members:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to add members';
      toast.error(errorMsg);
    }
  };

  const filteredUsers = availableUsers.filter(u =>
    (u.username || u.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-600">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-green-600 text-white p-4 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-xl font-semibold">Add Members</h1>
      </div>

      {/* Search */}
      <div className="p-4">
        <div className="relative">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>

        <div className="mt-3 flex items-center justify-between">
          {selectedUsers.length > 0 && (
            <div className="text-sm text-gray-600">
              {selectedUsers.length} member(s) selected
            </div>
          )}
          
          {filteredUsers.length > 0 && (
            <button
              onClick={handleSelectAll}
              className="ml-auto text-sm font-semibold text-green-600 hover:text-green-700 px-3 py-1 rounded-lg hover:bg-green-50 transition-colors"
            >
              {selectedUsers.length === filteredUsers.length ? '✓ Deselect All' : 'Select All'}
            </button>
          )}
        </div>
      </div>

      {/* Users List - with proper scrolling */}
      <div className="p-4 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 320px)', paddingBottom: '100px' }}>
        {filteredUsers.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No users available to add
          </div>
        ) : (
          filteredUsers.map((u) => (
            <div
              key={u.id}
              onClick={() => toggleUser(u.id)}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                selectedUsers.includes(u.id)
                  ? 'bg-green-100 border-2 border-green-600'
                  : 'bg-white hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedUsers.includes(u.id)}
                onChange={() => toggleUser(u.id)}
                onClick={(e) => e.stopPropagation()}
                className="w-5 h-5 text-green-600"
              />
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold">
                  {String(u.username || u.name || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <p className="font-medium">{String(u.username || u.name || 'Unknown')}</p>
                <p className="text-sm text-gray-500">
                  {u.role === 'super_admin' ? 'Admin' :
                   String(u.role || '').includes('handler') ? 'Handler' :
                   'Customer'}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg">
        <button
          onClick={handleAddMembers}
          disabled={selectedUsers.length === 0}
          className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-green-700 disabled:bg-gray-400 transition-colors"
        >
          <UserPlus size={20} weight="bold" />
          Add {selectedUsers.length > 0 ? `${selectedUsers.length} ` : ''}Member(s)
        </button>
      </div>
    </div>
  );
};

export default AddGroupMembers;